
import React, { useCallback, useMemo } from 'react'
import { type NodeProps, useNodeConnections, useNodesData } from '@xyflow/react'
import { PromptComposer } from '@/components/canvas/nodes/variants/PromptComposer'
import { type GenieNodeState, NODE_DIMENSIONS } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'
import { useSeriesDataStore } from '@/stores/useSeriesDataStore'

export interface PromptComposerNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

function PromptComposerNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as PromptComposerNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)

  const handleSelect = useCallback(() => {
    // Selection handled by React Flow
  }, [])

  // Read upstream connections reactively
  const incomingConnections = useNodeConnections({ handleType: 'target' })
  const sourceNodeIds = useMemo(
    () => incomingConnections.map((c) => c.source),
    [incomingConnections],
  )
  const sourceNodesData = useNodesData(sourceNodeIds)

  // Find upstream VariationGenerator node ID
  const upstreamVariationNodeId = useMemo(() => {
    for (const nodeEntry of sourceNodesData) {
      if (nodeEntry?.type === 'VariationGenerator') return nodeEntry.id
    }
    return null
  }, [sourceNodesData])

  // Read system prompt from connected text node on system-prompt-input handle
  const connectedSystemPrompt = useMemo(() => {
    const sysConn = incomingConnections.find((c) => c.targetHandle === 'system-prompt-input')
    if (!sysConn) return null
    const sourceData = sourceNodesData.find((n) => n?.id === sysConn.source)
    if (!sourceData) return null
    const state = (sourceData.data as Record<string, unknown>)?.state as Record<string, unknown> | undefined
    if (!state) return null
    return (state.prompt as string) || null
  }, [incomingConnections, sourceNodesData])

  // Read large data from external store (not React Flow node state)
  const connectedVariations = useSeriesDataStore(
    useCallback((s) => upstreamVariationNodeId ? s.variations.get(upstreamVariationNodeId) ?? null : null, [upstreamVariationNodeId])
  )

  const connectedCategoryNames = useMemo(() => {
    return connectedVariations?.[0]?.attributes?.map((a) => a.categoryName) ?? null
  }, [connectedVariations])

  const composerProps = useMemo(
    () => ({
      id,
      title: genieState.title,
      selected: selected || genieState.selected,
      width: NODE_DIMENSIONS.PromptComposer.width,
      height: NODE_DIMENSIONS.PromptComposer.height,
      onSelect: handleSelect,
      onHoverChange,
      onStateChange,
      state: genieState,
      connectedVariations,
      connectedCategoryNames,
      connectedSystemPrompt,
    }),
    [id, genieState, selected, handleSelect, onHoverChange, onStateChange, connectedVariations, connectedCategoryNames, connectedSystemPrompt],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.PromptComposer

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <PromptComposer {...composerProps} />
    </>
  )
}

export const PromptComposerNode = React.memo(
  PromptComposerNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as PromptComposerNodeData
    const nextData = nextProps.data as unknown as PromptComposerNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.composedPromptCount === nextData.state.composedPromptCount &&
      prevData.state.compositionStatus === nextData.state.compositionStatus &&
      prevData.state.compositionStats === nextData.state.compositionStats &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

PromptComposerNode.displayName = 'PromptComposerNode'
